import React, { Component } from 'react';
import SelectReact from 'react-select';
import { Box, Divider, Grid, IconButton, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';

import i18n from '../../i18n';
import AddAgentModal from './AddAgentModal';
import EditAgentModal from './EditAgentModal';
import GenericDeleteModal from './GenericDeleteModal';
import RequireScope from '../RequireScope';
import CustomValueContainer from '../ReactSelect/CustomValueContainer';
import CustomDropdownIndicator from '../ReactSelect/CustomDropdownIndicator';
import { groupAgents } from '../../helpers/helpers';
import { mapAgentEntities } from '../../helpers/optionFormatters';
import {
  addAgent,
  deleteAgent,
  fetchAgents,
  updateAgent,
} from '../../helpers/externalRequests';

import { customSettingsSelectStyles } from '../../assets/stylesheets/ReactSelectStyles';

class AgentEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAgent: {
        firstName: '',
        lastName: '',
        email: '',
        agencyId: 0,
        phoneNumber: { Home: '' }
      },
      selectedAgentId: 0,
      selectedAgentName: null,
      agents: [],
      showDeleteModal: false,
      showEditModal: false,
      showAddModal: false
    };

    this.editModalRef = React.createRef();
  }

  componentDidMount = () => {
    this.getAgents();
  };

  createAgent = async (agent) => {
    const { openSnackbar } = this.props;
    try {
      await addAgent({ agent });
      this.hideAddModal();
      openSnackbar(i18n.t('setting:successAgentCreate'));
      this.getAgents();
    } catch (error) {
      openSnackbar(`${i18n.t('setting:errorAgentCreate')}: ${error}`);
    }
  };

  deleteAgent = async (selectedAgentId) => {
    const { openSnackbar } = this.props;
    try {
      await deleteAgent({ id: selectedAgentId });
      this.hideDeleteModal();
      openSnackbar(i18n.t('setting:successAgentDelete'));
      this.setState({ selectedAgentName: null, selectedAgentId: 0 }, () =>
        this.getAgents()
      );
    } catch {
      openSnackbar(i18n.t('setting:errorAgentDelete'));
    }
  };

  getAgents = async () => {
    const { agencies } = this.props;
    const agents = await fetchAgents();
    const updatedAgents = agents.data.map(mapAgentEntities);
    const updatedGroupedAgents = agencies && agencies.length ? groupAgents(agencies, updatedAgents) : [];
    this.setState({ agents: updatedAgents, groupedAgents: updatedGroupedAgents });
  };

  updateEditAgent = () => {
    const { selectedAgent } = this.state;
    if (this.editModalRef.current) {
      this.editModalRef.current.updateSelectedAgent(selectedAgent);
    }
  }

  onAgentChange = event => {
    const value = event ? event.value : null;
    if (value) {
      this.setState(prevState => {
        const selectedAgentId = value;
        const selectedAgent = prevState.agents.find(
          agent => agent.id === value
        );
        return {
          selectedAgentName: selectedAgent.label,
          selectedAgentId,
          selectedAgent
        };
      }, () => this.updateEditAgent());
    }
  };

  saveAgent = async (agent) => {
    const { openSnackbar } = this.props;
    try {
      const response = await updateAgent({ agent });
      this.hideEditModal();
      if (response.data[0] === 1) {
        openSnackbar(i18n.t('setting:successAgentUpdate'));
        this.getAgents();
      } else {
        openSnackbar(i18n.t('setting:errorAgentUpdate'));
      }
    } catch (error) {
      openSnackbar(`${i18n.t('setting:errorAgentUpdate')} ${error}`);
    }
  };

  showDeleteModal = () => {
    this.setState({ showDeleteModal: true });
  };

  hideDeleteModal = () => {
    this.setState({ showDeleteModal: false });
  };

  showEditModal = () => {
    this.setState({ showEditModal: true });
  };

  hideEditModal = () => {
    this.setState({ showEditModal: false });
  };

  showAddModal = () => {
    const { agencies, openSnackbar } = this.props;
    if (agencies.length) {
      this.setState({ showAddModal: true });
    } else {
      openSnackbar(i18n.t('setting:errorNoAgencies'));
    }
  };

  hideAddModal = () => {
    this.setState({ showAddModal: false });
  };

  render() {
    const {
      selectedAgent,
      selectedAgentId,
      selectedAgentName,
      groupedAgents,
      showAddModal,
      showDeleteModal,
      showEditModal
    } = this.state;

    const { agencies } = this.props;

    const customComponents = {
      DropdownIndicator: CustomDropdownIndicator,
      IndicatorSeparator: null,
      ValueContainer: CustomValueContainer
    };

    return (
      <Grid container>
        <Grid item xs={7}>
          <h6>{i18n.t('setting:manageAgents')}</h6>
          <Divider />
          <br />
          <Box className="settings-box">
            <SelectReact
              fullWidth
              styles={customSettingsSelectStyles}
              components={customComponents}
              hideSelectedOptions
              placeholder={i18n.t('setting:chooseAgent')}
              menuShouldScrollIntoView={false}
              className="settings-select-field"
              options={groupedAgents}
              onChange={this.onAgentChange}
            />
            <Typography className="settings-in-box-typography" variant="caption" display="block" gutterBottom>{i18n.t('setting:agentHelp')}</Typography>
          </Box>
          <RequireScope scopes={['entities:write']}>
            <IconButton
              color="secondary"
              aria-label="add"
              onClick={this.showAddModal}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              aria-label="edit"
              color="secondary"
              onClick={this.showEditModal}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              color="secondary"
              onClick={this.showDeleteModal}
            >
              <DeleteIcon />
            </IconButton>
          </RequireScope>
        </Grid>
        <AddAgentModal
          showModal={showAddModal}
          onAddAgent={this.createAgent}
          onClose={this.hideAddModal}
          agencies={agencies}
        />
        <EditAgentModal
          ref={this.editModalRef}
          showModal={showEditModal}
          onClose={this.hideEditModal}
          onSave={this.saveAgent}
          selectedAgent={selectedAgent}
          agencies={agencies}
        />
        <GenericDeleteModal
          showModal={showDeleteModal}
          noSelectionTitle={i18n.t('setting:warningAgentModal')}
          confirmMessage={`${i18n.t(
            'setting:confirmDeleteAgent'
          )}: ${selectedAgentName}?`}
          selectedId={selectedAgentId}
          onClose={this.hideDeleteModal}
          onDelete={this.deleteAgent}
        />
      </Grid>
    );
  }
}

AgentEditor.propTypes = {
  openSnackbar: PropTypes.func.isRequired,
  agencies: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default AgentEditor;
